/**
 * Ein gesprochenes Stück als PCM-Strom über Web Audio. (02.10.2026, Ticket 2919, Stufe 1)
 *
 * ## Der Anlass
 *
 * Zwischen dem fertigen ersten Textstück und dem hörbaren Ton liegen heute im
 * Median 3,5 s (p90 5,7 s): Soniox rechnet das GANZE Stück, dann laufen fünf
 * ffmpeg-Prozesse, dann braucht ein `<audio>` rund 32 kB, bevor es losspielt.
 * Nachgemessen am 02.10. (24 Stücke): 3.507 ms (p90 5.687) bis zur fertigen
 * Datei; bei PCM liegt das erste Paket nach 298 ms da, 0,5 s Vorrat nach 340 ms
 * (p90 389).
 *
 * ## Wie
 *
 * `GET /api/tts/pcm?token=…` (server/tts-pcm.ts) liefert rohes PCM s16le/mono.
 * Hier wird es gelesen (`fetch().body.getReader()`), in Float32 gewandelt und als
 * `AudioBufferSourceNode`s LÜCKENLOS hintereinander geplant (`nextStart`). Die
 * Wiedergabe beginnt, sobald 0,5 s Vorrat da sind ODER der Strom zu Ende ist.
 *
 * ## Das alte Stottern — und warum es hier nicht wiederkommt
 *
 * Der Strom-Weg (tts-strom.ts) stotterte (25.09.): Soniox liefert nur mit der
 * 1,28-fachen Echtzeit, und bei `canplay` standen 0,19 s im Puffer. Dosis und
 * Wirkung (Stücke ab 2): vorgeladen 0,5 % gestört, 0,2–1 s 11 %, 1–3 s 70 %.
 * Zwei Folgerungen:
 *  - 0,5 s Vorrat vor dem Start (`START_VORRAT_S`) und
 *  - jedes Stück wird schon beim Einreihen heruntergeladen (`start()`), nicht
 *    erst, wenn es dran ist — spätere Stücke einer Antwort liegen fertig da.
 * Läuft der Vorrat trotzdem leer, ist das ein AUSSETZER (`AUSSETZER_MS`), er
 * wird gezählt, und `tts-pcm-rueckfall.ts` schaltet bei zu vielen auf den
 * Datei-Weg zurück.
 *
 * ## Die Klangkette (Ersatz für ffmpeg) — am 02.10. NACH Martins „komplett übersteuert“ neu
 *
 * ffmpeg macht auf dem Datei-Weg: Höhen-Shelf −2,5 dB bei 6 kHz, feste
 * Verstärkung, Spitzenbegrenzer (alimiter). Hier:
 *   Quelle → Biquad (highshelf, Werte aus der Kopfzeile) → Verstärkung →
 *   DynamicsCompressor als Begrenzer → Sicherheitskennlinie → Ausgang.
 *
 * ⚠️ Die erste Fassung kappte jede Spitze mit einer statischen tanh-Kennlinie
 *    (Knie 0,8). Gemessen an 10 echten Soniox-Sätzen (Tempo 1,2) in echtem
 *    Chromium: Sinus mit linearer Spitze 1,5 / 2,0 (so hoch liegen Sprachspitzen
 *    bei +8,6 dB) kam mit 16,5 % / 24,1 % Klirr heraus, und die Kette war mit
 *    −11,5 LUFS um 2,2 LU lauter als der Datei-Weg (−13,7 LUFS): jede laute
 *    Silbe wurde verformt. Jetzt: Begrenzer (threshold −6 dB, knee 0, ratio 20,
 *    attack 3 ms, release 100 ms) — Klirr 0,01 % — und eine Verstärkung, die
 *    auf die Lautheit der Datei zielt (`browserVerstaerkung`).
 *
 * ⚠️ ABWEICHUNGEN vom ffmpeg-Ergebnis:
 *    1. Der DynamicsCompressorNode ist kein Lookahead-Brickwall wie alimiter
 *       (6 ms Vorlauf, ratio 20): einzelne Spitzen schießen leicht über (1,008
 *       gemessen). Die Sicherheitskennlinie fängt sie (identisch bis 0,9, darüber
 *       tanh gegen 0,999): 0 Samples ≥ 0,999, 0,003 % über 0,95.
 *    2. Der Kompressor bringt seinen EIGENEN Pegelausgleich mit (Chrome und
 *       WebKit rechnen ihn gleich; ohne Verstärkung hebt er die Lautheit um rund
 *       3 dB). Darum zieht `browserVerstaerkung` die Kopfzeilen-Verstärkung um
 *       `PCM_ABZUG_DB` herunter — gemessen, nicht gerechnet. Ob WebKit auf dem
 *       iPhone exakt gleich klingt, ist NICHT geprüft.
 *    3. Das Shelf des Browsers ist ein Biquad-highshelf (Steigung fest); ffmpegs
 *       `treble` läuft mit Güte 0,7. Gleiche Absenkung, leicht andere Flanke.
 *    4. Die Verstärkung ist der feste Wert der Stimme (Median der letzten
 *       Rohpegel, server/tts-stream.ts), nicht der Pegel dieses Stücks.
 *    Der Zwischenspeicher-MP3 des Servers trägt die echte ffmpeg-Kette; Datei
 *    und Strom klingen gleich laut (−13,6 gegen −13,7 LUFS), aber nicht bit-gleich.
 *
 * ## Web Audio statt `<audio>` — Risiken (Bericht, nicht hier lösbar)
 *
 * - iOS: ein AudioContext startet gesperrt; er wird in einer Geste entsperrt
 *   (`entsperrePcm`, aufgerufen von `unlockAudio` und beim Einschalten des
 *   Schalters). Geht es nicht, läuft das Stück über den Datei-Weg.
 * - Stumm-Schalter und Tonsitzung: Web Audio hängt an derselben nativen
 *   Tonsitzung wie `<audio>`, folgt aber dem Klingelschalter anders — am
 *   Gerät zu prüfen.
 * - Echo-Unterdrückung und „Unterbrechen" (Ticket 2695): ein `<audio>` ist für
 *   den Browser ein Medium-Element, ein AudioContext ein Graph; wie das
 *   Mikrofon beides vom Ton trennt, kann sich unterscheiden.
 */

import { loadSettings } from "./settings.js";
import { mikroBeimVorlesenAn, mikroBeimVorlesenAuffrischen } from "./mikro-beim-vorlesen.js";
import { gesteDarfWecken, setzeMotorPruefer } from "./geste-wache.js";

/** So viel Ton muss da sein, bevor es losgeht — das Gegenmittel zum Stottern. */
export const START_VORRAT_S = 0.5;
/**
 * So lange (ms) wartet ein Stück auf einen GRÖSSEREN Vorrat als `START_VORRAT_S` (tts-pcm-vorrat.ts: nach einem Aussetzer in
 * der Antwort 1,5–2,5 s), gezählt ab dem Augenblick, in dem der Mindest-Vorrat da war. Ein langsamer Strom macht die Antwort
 * so nicht zäh — er spielt dann mit dem, was da ist.
 */
export const VORRAT_FRIST_MS = 2_000;
/** Eine Lücke im Vorrat ab dieser Länge zählt als Aussetzer. */
export const AUSSETZER_MS = 100;
/** Kleiner Vorlauf vor dem ersten Block (s): der Graph braucht einen Augenblick. */
const ANLAUF_S = 0.03;
/**
 * So lange wartet ein Stück, das spielen soll, auf sein ERSTES Paket (Ticket 2919, Nachprüfung):
 * ein Antwortkopf ohne Daten oder ein `fetch`, das nie zurückkehrt, hielte sonst die Warteschlange für immer an.
 * Gezählt ab dem Beginn des Wartens, nicht ab dem Vorladen — ein Stück, das hinter anderen auf einen
 * Soniox-Platz wartet, darf dort stehen.
 */
export const ERSTES_PAKET_FRIST_MS = 7_000;
/** So lange darf der Strom schweigen, bevor das Stück als gerissen gilt. */
export const STROM_STILLE_MS = 8_000;
/** So lange darf der Kontext nach dem Entsperren „nicht laufend" sein, bevor das Stück den Datei-Weg nimmt. */
const KONTEXT_FRIST_MS = 400;
/**
 * Dieselbe Frist, wenn der Kontext in der App absichtlich ruhte (`kontextRuhen`): das Aufwecken ist dann der
 * Normalfall jeder Antwort und darf am iPhone etwas länger dauern, ohne dass das Stück als Fehler auf die Datei geht.
 */
export const WECK_FRIST_MS = 1_200;
/**
 * So viele Weck-Fristen in Folge, bis EINE davon als Fehler vor dem Start zählt (Nachprüfung Opus, K1): eine einzelne
 * langsame Weckung ist Alltag am iPhone und darf PCM nicht abschalten; drei hintereinander deuten auf ein
 * dauerhaft kaputtes Wecken.
 */
const WECK_FRIST_FOLGE_ZAEHLT = 3;
/** Standard-Abtastrate des Servers (SONIOX_TTS_RATE). Die Kopfzeile `X-Tts-Rate` gilt, falls sie abweicht. */
export const PCM_RATE_STANDARD = 24_000;
/**
 * Um so viel liegt die Verstärkung des Browsers unter der der Kopfzeile (die auf −11 LUFS ZIELT, ohne Begrenzer).
 * Gemessen an 10 echten Sätzen: Kopfzeile 8,6 dB → 6,0 dB vor dem Kompressor ergibt −13,6 LUFS, der Datei-Weg −13,7.
 */
export const PCM_ABZUG_DB = 2.6;
/** Obergrenze der Browser-Verstärkung (dB) — auch bei einer unsinnig hohen Kopfzeile. */
export const PCM_MAX_VERSTAERKUNG_DB = 7;
/** Der Begrenzer (DynamicsCompressorNode): threshold, knee, ratio, attack, release. */
export const LIMITER = { threshold: -6, knee: 0, ratio: 20, attack: 0.003, release: 0.1 } as const;
/** Die Sicherheitskennlinie hinter dem Begrenzer: bis hierher unverändert … */
export const SICHER_KNIE = 0.9;
/** … darüber tanh gegen diese Decke (fängt nur das, was der Begrenzer überschießt). */
export const SICHER_DECKE = 0.999;

/*
 * ⚠️ Messfühler „Ton ist da, aber nichts zu hören" (Ticket 2919, Nachtrag 12:28). Martin: „Text kam schnell, aber keine
 * Audioausgabe" — das Journal zeigte 18 s Ton ohne Aussetzer. Der Browser meldete also ein gespieltes Stück, der Mensch
 * hörte nichts. Drei Möglichkeiten (Stummschalter und Tonsitzung, Sitzung nach der Sprechtaste auf Aufnahme, Pegel null);
 * gemessen wird, nicht geraten: je Strom der Pegel HINTER der Kette (AnalyserNode), Zustand und Latenzen des Kontexts,
 * Typ der Tonsitzung. Nur Zahlen.
 */
/** Ein Analyse-Fenster hat so viele Samples (≈ 43 ms bei 24 kHz). */
const ANALYSE_FENSTER = 1024;

/** Effektivwert (RMS) des Messfensters hinter der Kette — für die Anzeige am Kreis (V9), nicht für Entscheidungen. */
function rmsVon(analyse: AnalyserNode, abtast: Float32Array<ArrayBuffer>): number {
  analyse.getFloatTimeDomainData(abtast);
  let sq = 0;
  for (let i = 0; i < abtast.length; i++) sq += abtast[i] * abtast[i];
  return Math.sqrt(sq / abtast.length);
}
/** Unter dieser Spitze (≈ −80 dBFS) gilt ein Fenster als stumm, wenn laut gespielt werden müsste. */
const STUMM_SPITZE = 1e-4;
/** So laut (Effektivwert) muss der Block am Eingang mindestens sein, damit ein stummes Fenster etwas bedeutet. */
const STUMM_BLOCK_RMS = 0.005;
/** So lange (ms) muss der Ausgang durchgehend stumm sein, bevor das Stück auf die Datei wechselt. */
const STUMM_MS = 450;
/** So lange (ms) darf die Uhr des Kontexts stehen, obwohl er „running" meldet und Ton eingeplant ist. */
const UHR_STEHT_MS = 700;

interface TonsitzungLike {
  type: string;
}

/** `navigator.audioSession` (iOS 16.4+, Safari) — oder null. */
function tonsitzung(): TonsitzungLike | null {
  try {
    const s = (typeof navigator === "undefined" ? undefined : (navigator as unknown as { audioSession?: TonsitzungLike }).audioSession);
    return s && typeof s.type === "string" ? s : null;
  } catch {
    return null;
  }
}

let sitzungVorher: string | null = null;
let sitzungHalter = 0;
let sitzungTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Die Tonsitzung für die Dauer eines PCM-Stücks auf `playback` stellen (falls es sie gibt): Web Audio folgt am
 * iPhone dem Stummschalter, ein `<audio>` nicht — mit `playback` nicht mehr. Der Datei-Weg fasst die Sitzung nicht an
 * (nirgends im Frontend steht `audioSession`); hier muss sie darum nach dem Stück ZURÜCK, sonst bliebe sie für die
 * Sprechtaste auf `playback`. Zurück: sofort bei `tts-audio-end`/`-abbruch` (die Sprechtaste bricht ab), sonst 1,5 s nach
 * dem letzten Stück.
 */
export function sitzungHalten(): void {
  sitzungHalter += 1;
  kontextRuht = false;
  ruheNachLoslassen = false;
  ausklangStopp();
  if (sitzungTimer) {
    clearTimeout(sitzungTimer);
    sitzungTimer = null;
  }
  const s = tonsitzung();
  if (!s) return;
  /*
   * 13.10.2026 (Bahn ios-mikro-offen): Hält die native App das Mikro beim Vorlesen offen (Kippe `mikroBeimVorlesen`
   * im Audio-Zustand, Build 106+), darf die Seite die Sitzung NICHT auf `playback` stellen — das nähme dem nativen
   * Motor den Eingang (Mikrosymbol weg, „Hey Vespera" taub). Ohne App, ohne Feld (Build 105) oder bei AUS: wie bisher.
   */
  if (inDerApp()) {
    mikroBeimVorlesenAuffrischen();
    if (mikroBeimVorlesenAn()) return;
  }
  try {
    if (sitzungVorher === null) sitzungVorher = s.type;
    if (s.type !== "playback") s.type = "playback";
  } catch {
    /* ältere WebKit-Fassungen: kein Schreiben */
  }
}

export function sitzungLoslassen(): void {
  sitzungHalter = Math.max(0, sitzungHalter - 1);
  // Kam das Ende der Schlange schon (Abbruch: das Ereignis läuft vor dem `finally` des Stücks), gleich zurück.
  if (sitzungHalter === 0 && ruheNachLoslassen) {
    sitzungZurueck();
    return;
  }
  if (sitzungHalter > 0 || sitzungTimer) return;
  sitzungTimer = setTimeout(sitzungZurueck, 1500);
}

/**
 * Vor JEDER Mikro-Aufnahme (V9: `holeMikroStrom`): die Tonsitzung so stellen, dass iOS die Aufnahme erlaubt (15.11.2026, Version 4054:
 * „AudioSession category is not compatible with audio capture“). `playback` verweigert `getUserMedia`; das PCM-Stück setzt es und der
 * Rückstell-Timer holt es erst 1,5 s nach dem letzten Stück zurück. Hier: Timer weg, Typ zurück auf den Stand von vorher — und steht er
 * danach noch auf `playback` (oder war er es schon vorher), auf `play-and-record`. `sitzungHalten()` setzt beim nächsten Stück wieder
 * `playback`. Ohne `navigator.audioSession` (Desktop, Android): nichts.
 */
export function sitzungFuerAufnahme(): void {
  const s = tonsitzung();
  if (!s) return;
  /** Typ auf play-and-record (sonst verweigert iOS die Aufnahme); true, wenn er vorher `playback` war. */
  const aufnahmeTyp = (): boolean => {
    try {
      if (s.type !== "playback") return false;
      s.type = "play-and-record";
      return true;
    } catch {
      return false; // ältere WebKit-Fassungen: kein Schreiben
    }
  };
  // K3: Spielt noch ein Stück, nur der Typ, kein Zurückstellen mitten im Stück.
  if (sitzungHalter > 0) return void aufnahmeTyp();
  sitzungTypZurueck();
  // K1: Timer weg, `sitzungZurueck` läuft nicht: Kontext selbst schlafen legen.
  ausklangStopp();
  kontextRuhen();
  // K2: kein Ausgangsstand gemerkt: nächstes Rückstellen auf „auto“.
  if (aufnahmeTyp() && sitzungVorher === null) sitzungVorher = "auto";
}

/** Die Tonsitzung auf den Stand von vor dem PCM-Weg zurück. Der Kontext bleibt, wie er ist. */
function sitzungTypZurueck(): void {
  if (sitzungTimer) {
    clearTimeout(sitzungTimer);
    sitzungTimer = null;
  }
  const s = tonsitzung();
  if (s && sitzungVorher !== null) {
    try {
      s.type = sitzungVorher;
    } catch {
      /* egal */
    }
  }
  sitzungVorher = null;
  ruheNachLoslassen = sitzungHalter > 0;
}

/** Die Tonsitzung auf den Stand von vor dem PCM-Weg zurück — und in der App den Kontext SOFORT schlafen legen. */
export function sitzungZurueck(): void {
  ausklangStopp();
  sitzungTypZurueck();
  kontextRuhen();
}

/**
 * So lange läuft der Kontext in der App nach dem ENDE eines Zuges weiter, bevor er schläft. (Ticket 2941)
 *
 * `suspend()` hält die Ausgabe an, und was der Kontext schon gerechnet, aber noch nicht an den Lautsprecher abgegeben
 * hat, geht verloren — der Schlusslaut der Antwort („fet" statt „fertig", Ticket 2764 am `<audio>` über Bluetooth).
 * Der PCM-Weg meldet sein Ende, sobald die UHR des Kontexts das Ende des Tons erreicht hat; bis das Ende hörbar ist,
 * vergeht die Latenz des Ausgangs. Die meldet der Kontext selbst (`outputLatency`, im Journal als `aus=` der
 * Pegelzeile; 08.–11.10.: iPhone-Lautsprecher 16 ms in 960 Zeilen, Mac 40 ms in 278, 127 ms in 22 — vermutlich eine
 * Bluetooth-Strecke). Gemessen im echten Chromium: `suspend()` lief 0,2 ms nach `tts-audio-end`, und in einem von vier
 * Läufen waren die letzten 39 ms noch nicht hörbar (`getOutputTimestamp()`).
 *
 * Wartezeit = `outputLatency` + `baseLatency` + 150 ms, nach unten `KONTEXT_AUSKLANG_MIN_MS`, nach oben
 * `KONTEXT_AUSKLANG_MS`; meldet der Kontext keine Latenz (Safari kennt `outputLatency` nicht überall), gilt die Obergrenze.
 * Der benutzte Wert steht als `ausklang=` in der Pegelzeile (`[tts-pcm] pegel`) — so ist messbar, wie lang das Fenster war.
 *
 * ⚠️ KÜRZER als der Ausklang des Mikros (`AUSKLANG_MS`, 600 ms, ausklang.ts): so schläft der Kontext, bevor das
 *    selbstöffnende Mikro nach einer Frage aufgeht. Und weit unter der Miete des Seitentons (3 s Nachlauf,
 *    seiten-ton.ts): das Native startet seinen Mikrofon-Motor erst danach, der Kontext schläft also vor ihm.
 * ⚠️ Die Tonsitzung selbst geht trotzdem SOFORT zurück (wie bisher): bliebe sie auf `playback`, bekäme eine
 *    Sprechtaste in dieser Zeit Stille (`playback` kann nicht aufnehmen). Nur das Schlafenlegen wartet. OFFEN, nicht
 *    gemessen: ob der Wechsel von `playback` auf `auto` bei nativ gepinnter Sitzung den Schlusslaut trotz Ausklang kappt.
 * ⚠️ Der Ausklang endet SOFORT (`ausklangBeenden`), wenn etwas anderes die Sitzung anfasst: Abbruch (`tts-audio-abbruch`,
 *    neue Antwort), die Seite geht in den Hintergrund, die Sprechtaste beginnt (`starteAufnahme` in ptt.ts — ihr
 *    `setSeitenAufnahme(true)` schaltet die native Sitzung um, ein `suspend()` mitten darin war Ticket 2524; Netz:
 *    `voice-v8:web-mikro` mit `offen`) — und eine Rückkehr oder Berührung WECKT einen Kontext im Ausklang nicht (iOS
 *    kann ihn dann unterbrochen haben, das Muster vom 02.10.: `nimmKontextWiederAuf`).
 */
export const KONTEXT_AUSKLANG_MS = 500;
/** Untergrenze der Wartezeit: auch bei kleiner gemeldeter Latenz hat die Hardware-Strecke etwas Nachlauf. */
export const KONTEXT_AUSKLANG_MIN_MS = 150;
/** Zuschlag auf die gemeldete Latenz (ms). */
const KONTEXT_AUSKLANG_ZUSCHLAG_MS = 150;
let ausklangUhr: ReturnType<typeof setTimeout> | null = null;

/** Wie lange der Kontext nach dem Ende ausklingt — aus den Latenzen, die er selbst meldet. */
export function ausklangDauerMs(c: { outputLatency?: number; baseLatency?: number } | null | undefined): number {
  const aus = c?.outputLatency;
  if (typeof aus !== "number" || !Number.isFinite(aus) || aus <= 0) return KONTEXT_AUSKLANG_MS;
  const basis = typeof c?.baseLatency === "number" && Number.isFinite(c.baseLatency) && c.baseLatency > 0 ? c.baseLatency : 0;
  const ms = Math.round((aus + basis) * 1000) + KONTEXT_AUSKLANG_ZUSCHLAG_MS;
  return Math.min(KONTEXT_AUSKLANG_MS, Math.max(KONTEXT_AUSKLANG_MIN_MS, ms));
}

function ausklangStopp(): void {
  if (!ausklangUhr) return;
  clearTimeout(ausklangUhr);
  ausklangUhr = null;
}

/** Läuft gerade ein Ausklang (der Kontext ist noch wach, soll aber schlafen)? */
export function ausklangLaeuft(): boolean {
  return ausklangUhr !== null;
}

/** Den Ausklang JETZT beenden: der Kontext schläft sofort. Ohne laufenden Ausklang tut es nichts. */
export function ausklangBeenden(): void {
  if (!ausklangUhr) return;
  ausklangStopp();
  kontextRuhen();
}

/**
 * Das normale Ende eines Zuges: Tonsitzung sofort zurück, den Kontext erst nach dem Ausklang schlafen legen.
 * Beginnt inzwischen ein Stück (`sitzungHalten`), entfällt das Schlafenlegen; hält eines die Sitzung, tut `kontextRuhen` nichts.
 */
export function sitzungAusklingen(): void {
  ausklangStopp();
  sitzungTypZurueck();
  if (!inDerApp()) return;
  ausklangUhr = setTimeout(() => {
    ausklangUhr = null;
    kontextRuhen();
  }, ausklangDauerMs(geteilterKontext));
}

/*
 * ⚠️⚠️ 02.10.2026 — DER STILLE KONTEXT NAHM DEM WACH-WORT DAS MIKROFON.
 *
 * Martin, 15:57: „Hey Vespera geht gar nicht mehr. Das Mikro ist immer aus.“ Der geteilte AudioContext blieb nach
 * jeder Antwort `running` (336 von 336 Pegelzeilen `state=running`, nie ein `suspend()`), und jede Berührung
 * (`entsperrePcm`) und jede Rückkehr der Seite (`nimmKontextWiederAuf`) weckte ihn wieder, sobald iOS ihn
 * unterbrochen hatte. Ein laufender Web-Audio-Kontext ist für iOS Ton der Seite: er nimmt dem Mikrofon-Motor der
 * App (PawbotMikroMotor) die Tonsitzung — OHNE dass die Seite eine Wiedergabe meldet. Journal 02.10.:
 *  - Motorstarts, die binnen 5 s ohne gemeldete Ruhe wieder „unterbrochen“ waren: 08:00–10:43 (ohne PCM) 0 von 25,
 *    10:43–16:00 (mit PCM) 8 von 25 — alle beim Zurückkommen der Seite (10:59:50, 11:14:27, 11:21:57, 14:26:43,
 *    14:37:01, 15:23:28).
 *  - Build 95 startet nach einer Unterbrechung nur von selbst neu, wenn der Ton der Seite sie auslöste (eine Ruhe
 *    „wiedergabe“/„web-mikro“); diese hier hatten keine → der Motor wartete auf ein Ende, das iOS nie schickt:
 *    10:59:54 → 11:12:30 (756 s) ohne Wach-Wort.
 *
 * Darum: IN DER APP schläft der Kontext, sobald kein PCM-Stück ihn hält (Ende/Abbruch der Schlange sofort, sonst
 * 1,5 s nach dem letzten Stück), und weder Berührung noch Rückkehr wecken einen schlafenden. Geweckt wird er nur von
 * einem Stück, das spielen will (`spieleInnen`, mit `WECK_FRIST_MS`). In der App braucht Ton keine Geste
 * (Capacitor: `mediaTypesRequiringUserActionForPlayback = []`; audio-feedback.ts: 1302 Wiedergaben, 0 Fehler).
 * Im Browser bleibt alles wie bisher — dort gibt es keinen Motor, aber die Autoplay-Sperre.
 */
let kontextRuht = false;
/** Das Versprechen des letzten `suspend()` — ein Stück, das weckt, wartet erst darauf (K4: suspend noch unterwegs). */
let schlafVersprechen: Promise<void> | null = null;
/** Wie viele Stücke hintereinander in der Weck-Frist hängen blieben (K1). */
let weckFristFolge = 0;
/** Das Ende der Schlange kam, während ein Stück die Sitzung noch hielt — beim Loslassen sofort ruhen. */
let ruheNachLoslassen = false;

/** Läuft die Seite in der nativen App? (wie playback-hifi.ts, ohne Import) */
function inDerApp(): boolean {
  const c = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return c?.isNativePlatform?.() === true;
}

/** In der App: den geteilten Kontext schlafen legen, wenn kein Stück ihn hält. Wirft nie. */
export function kontextRuhen(): void {
  if (!inDerApp() || sitzungHalter > 0) return;
  const c = geteilterKontext;
  if (!c || c.state === "closed") return;
  kontextRuht = true;
  // IMMER `suspend()`, auch wenn er schon `suspended` meldet (K2): es reiht sich hinter ein noch hängendes
  // `resume()` — sonst liefe der Kontext nach dessen spätem Ende still weiter.
  try {
    schlafVersprechen = c.suspend().catch(() => undefined);
  } catch {
    schlafVersprechen = null;
  }
}

/** In der App: wie lange ein Stück auf das Aufwachen des Kontexts wartet (beim ersten Stück nach dem Start ebenso, K3). */
function weckFrist(): number {
  return inDerApp() ? WECK_FRIST_MS : KONTEXT_FRIST_MS;
}

/** Schläft der Kontext absichtlich (App, kein Stück)? Für Tests und die Pegelzeile. */
export function kontextSchlaeft(): boolean {
  return kontextRuht && sitzungHalter === 0;
}

// Der Motor hält die App-Tonsitzung, solange der Kontext absichtlich schläft (geste-wache.ts).
setzeMotorPruefer(kontextSchlaeft);

/** „auto>playback" während des Stücks, „auto" sonst, „fehlt" ohne die Schnittstelle — für das Journal. */
function sitzungBericht(): string {
  const s = tonsitzung();
  if (!s) return "fehlt";
  return sitzungVorher !== null && sitzungVorher !== s.type ? `${sitzungVorher}>${s.type}` : s.type;
}

/** Nur Tests. */
export function _sitzungZuruecksetzen(): void {
  sitzungVorher = null;
  sitzungHalter = 0;
  kontextRuht = false;
  ruheNachLoslassen = false;
  schlafVersprechen = null;
  weckFristFolge = 0;
  if (sitzungTimer) clearTimeout(sitzungTimer);
  sitzungTimer = null;
  ausklangStopp();
}

if (typeof window !== "undefined") {
  // Die Sprechtaste bricht die Vorlesung ab (`tts-audio-abbruch`) — dann MUSS die Sitzung sofort wieder frei sein,
  // bevor das Mikrofon aufgeht, und der Ton endet gleich. Das normale Ende gibt die Sitzung auch sofort zurück, lässt
  // den Kontext aber noch ausklingen (`sitzungAusklingen`, Ticket 2941).
  window.addEventListener("voice-v8:tts-audio-end", () => sitzungAusklingen());
  window.addEventListener("voice-v8:tts-audio-abbruch", () => sitzungZurueck());
  // Ticket 2941: wer sonst die Sitzung anfasst, beendet den Ausklang. Die Sprechtaste ruft `ausklangBeenden` selbst
  // (ptt.ts, `starteAufnahme`, vor `setSeitenAufnahme`); das Mikro der Seite ist das Netz dahinter.
  window.addEventListener("voice-v8:web-mikro", (ev) => {
    if ((ev as CustomEvent<{ offen?: boolean }>).detail?.offen === true) ausklangBeenden();
  });
  try {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") ausklangBeenden();
    });
  } catch {
    /* kein document (Tests ohne DOM) */
  }
}

/** Kommt dieses Stück über den PCM-Weg (/api/tts/pcm)? */
export function istPcmAdresse(url: string): boolean {
  return /\/api\/tts\/pcm\?/.test(url);
}

/**
 * Dieselbe Anfrage als gepufferte Datei — der Rückfall. Der Server hält für
 * den PCM-Strom die Render-Sperre dieses Satzes: ein `puffer=1`-Abruf wartet
 * auf die mitgeschnittene Datei, statt neu zu vertonen.
 */
export function pcmDateiUrl(url: string): string {
  const [ohneAnker, anker] = url.split("#", 2) as [string, string | undefined];
  const stream = ohneAnker.replace("/api/tts/pcm?", "/api/tts/stream?");
  const mitPuffer = /[?&]puffer=1(?:&|$)/.test(stream) ? stream : `${stream}&puffer=1`;
  return anker ? `${mitPuffer}#${anker}` : mitPuffer;
}

/** Die PCM-Adresse aus einem Token. */
export function pcmAdresseAusToken(token: string): string {
  return `/api/tts/pcm?token=${encodeURIComponent(token)}`;
}

/** Die Adresse des Abrufs: mit `&vorab=1`, solange das Stück als Vorabladung ohne Vorrang lädt (17.10.2026). Rein. */
export function pcmAbrufAdresse(url: string, vorab: boolean): string {
  return vorab ? `${url}${url.includes("?") ? "&" : "?"}vorab=1` : url;
}

/** Nimmt dieser Satz den PCM-Weg? Rein, damit es prüfbar ist. */
export function pcmWegFuer(o: {
  beta: boolean;
  voiceId: string;
  /** Vorlese-Tempo aus den Einstellungen. */
  tempo?: number;
  /** Hat die Sitzung schon auf den Datei-Weg zurückgeschaltet? */
  rueckfall: boolean;
  /** Gibt es Web Audio in dieser Umgebung? */
  webAudio: boolean;
  /** Liegt die Seite im Hintergrund? Dann spielt der native Spieler eine Datei. */
  sichtbar: boolean;
}): boolean {
  if (!o.beta || o.rueckfall || !o.webAudio || !o.sichtbar) return false;
  if (!o.voiceId.startsWith("soniox:")) return false;
  // Soniox nimmt `speed` nur von 0,7 bis 1,3. Darüber bleibt der alte Weg (atempo bis 2,0).
  return o.tempo === undefined || (Number.isFinite(o.tempo) && o.tempo >= 0.7 && o.tempo <= 1.3);
}

/**
 * Wandelt s16le-Bytes in Float32. Ein Netzbrocken endet beliebig — liegt am Ende
 * ein halbes Sample, bleibt sein erstes Byte als `rest` liegen und wird dem
 * nächsten Brocken vorangestellt.
 */
export function pcmZuFloat(
  bytes: Uint8Array,
  rest: number | null,
): { samples: Float32Array; rest: number | null } {
  const wert = (v: number): number => (v >= 0x8000 ? v - 0x10000 : v) / 32768;
  const gesamt = (rest !== null ? 1 : 0) + bytes.length;
  const out = new Float32Array(Math.floor(gesamt / 2));
  let o = 0;
  let i = 0;
  if (rest !== null) {
    if (bytes.length === 0) return { samples: out, rest };
    out[o++] = wert(rest | ((bytes[0] ?? 0) << 8));
    i = 1;
  }
  for (; i + 1 < bytes.length; i += 2) {
    out[o++] = wert((bytes[i] ?? 0) | ((bytes[i + 1] ?? 0) << 8));
  }
  return { samples: out, rest: i < bytes.length ? (bytes[i] ?? 0) : null };
}

/** Die Verstärkung des Browsers aus der Kopfzeile: Zielpegel des Datei-Wegs, mit dem Begrenzer dahinter. */
export function browserVerstaerkung(kopfzeileDb: number): number {
  if (!Number.isFinite(kopfzeileDb)) return 0;
  return Math.min(PCM_MAX_VERSTAERKUNG_DB, Math.max(0, kopfzeileDb - PCM_ABZUG_DB));
}

/** Identisch bis `SICHER_KNIE`, darüber tanh gegen `SICHER_DECKE` — nur ein Netz unter dem Begrenzer. */
export function sicherheitsKennlinie(x: number): number {
  const a = Math.abs(x);
  if (a <= SICHER_KNIE) return x;
  const y = SICHER_KNIE + (SICHER_DECKE - SICHER_KNIE) * Math.tanh((a - SICHER_KNIE) / (SICHER_DECKE - SICHER_KNIE));
  return x < 0 ? -y : y;
}

/** Die Kurve des WaveShapers (Eingang −1…1, Rohwerte darüber klemmt er auf den Rand). */
export function sicherheitsKurve(punkte = 4096): Float32Array<ArrayBuffer> {
  const kurve = new Float32Array(punkte);
  for (let i = 0; i < punkte; i++) kurve[i] = sicherheitsKennlinie((i / (punkte - 1)) * 2 - 1);
  return kurve;
}

/** Ein gemeinsamer AudioContext für alle PCM-Stücke. */
let geteilterKontext: AudioContext | null = null;
let entsperrHoerer = false;

interface AudioContextCtor {
  new (o?: AudioContextOptions): AudioContext;
}

/** Gibt es in dieser Umgebung Web Audio? */
export function webAudioDa(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return typeof window.AudioContext === "function" || typeof w.webkitAudioContext === "function";
}

/**
 * Der Kontext dieser Seite — in der Rate des Servers, damit KEIN Stück einzeln
 * umgerechnet wird (an den Nähten klickt das). Die Ausgabe-Hardware rechnet
 * den einen durchlaufenden Strom ohnehin um.
 */
export function pcmKontext(rate: number = PCM_RATE_STANDARD): AudioContext | null {
  if (geteilterKontext && geteilterKontext.state !== "closed") return geteilterKontext;
  if (!webAudioDa()) return null;
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  const Ctor: AudioContextCtor | undefined = window.AudioContext ?? w.webkitAudioContext;
  if (!Ctor) return null;
  try {
    geteilterKontext = new Ctor({ sampleRate: rate });
  } catch {
    try {
      geteilterKontext = new Ctor();
    } catch {
      return null;
    }
  }
  installiereEntsperrung();
  wacheGegenSelbstWecken(geteilterKontext);
  return geteilterKontext;
}

/**
 * K2: Läuft der Kontext, obwohl er schlafen soll (WebKit nimmt ihn nach einer Unterbrechung selbst wieder auf, ein
 * `resume()` löst erst nach der Frist auf), wird er sofort wieder schlafen gelegt. Nur in der App wirksam
 * (`kontextSchlaeft` ist im Browser nie wahr).
 */
function wacheGegenSelbstWecken(c: AudioContext): void {
  try {
    c.addEventListener("statechange", () => {
      if (c === geteilterKontext && (c.state as string) === "running" && kontextSchlaeft()) kontextRuhen();
    });
  } catch {
    /* ältere WebKit-Fassungen: kein Ereignis — dann bleibt es bei Ende und Frist */
  }
}

/**
 * Den Kontext schließen — der Schalter wurde ausgeschaltet. Ein offener Kontext hielte die Tonsitzung
 * des Telefons weiter fest, obwohl der Weg aus ist. Beim nächsten Einschalten entsteht ein neuer.
 */
export function schliessePcmKontext(): void {
  const c = geteilterKontext;
  geteilterKontext = null;
  if (!c || c.state === "closed") return;
  try {
    void c.close().catch(() => undefined);
  } catch {
    /* schon zu */
  }
}

/** Nur Tests. */
export function _pcmKontextZuruecksetzen(): void {
  geteilterKontext = null;
  nutzerPause = false;
  kontextRuht = false;
  schlafVersprechen = null;
  weckFristFolge = 0;
}

/**
 * Nutzer-Pause der Seite /neun: solange sie gilt, weckt weder eine Geste (Taste, Berührung, Klick daneben) noch die
 * Rückkehr in den Tab den geteilten Kontext — sonst liefe der Ton hörbar weiter, während die Anzeige „Pause“ zeigt.
 * Gesetzt von der Pause, gelöscht von Weiter, Ende, Abbruch und jedem ausdrücklichen `entsperrePcm(true)` (neuer Zug).
 */
let nutzerPause = false;
export function setzePcmNutzerPause(an: boolean): void {
  nutzerPause = an;
}

/** Jede spätere Geste weckt einen schlafenden Kontext — einmal entsperrt ist nicht für immer entsperrt (iOS). */
function installiereEntsperrung(): void {
  if (entsperrHoerer || typeof document === "undefined") return;
  entsperrHoerer = true;
  const opts = { capture: true, passive: true } as const;
  for (const ev of ["pointerdown", "keydown", "touchstart"]) {
    try {
      document.addEventListener(ev, () => entsperrePcm(), opts);
    } catch {
      /* ein fehlender Ereignistyp darf nie stören */
    }
  }
  // Ticket 2919 (e): Kommt die Seite zurück, nimmt der Kontext seine Arbeit wieder auf — iOS schickt ihn beim Sperren
  // in `interrupted`/`suspended`. Ohne das wäre jede folgende Antwort an der Frist des ersten Stücks gescheitert.
  try {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") nimmKontextWiederAuf();
    });
  } catch {
    /* egal */
  }
}

/** Würde `nimmKontextWiederAuf` jetzt wirklich `resume()` rufen? (gleiche Bedingungen, ohne Wirkung) */
function wuerdeGesteWecken(): boolean {
  const c = geteilterKontext;
  if (!c || c.state === "closed" || (c.state as string) === "running") return false;
  return !kontextSchlaeft() && !ausklangLaeuft();
}

/** Den geteilten Kontext aufwecken, falls er nicht läuft (nicht, wenn er geschlossen ist). Wirft nie. */
function nimmKontextWiederAuf(): void {
  const c = geteilterKontext;
  if (nutzerPause || !c || c.state === "closed" || (c.state as string) === "running") return;
  // In der App: ein schlafender Kontext bleibt liegen, bis ein Stück spielen will (siehe `kontextRuhen`).
  if (kontextSchlaeft()) return;
  // Ticket 2941: ein Kontext im Ausklang IST auf dem Weg in den Schlaf. Hat iOS ihn dort unterbrochen (Sperren, Siri,
  // Mitteilung), wird er nicht geweckt (das Muster vom 02.10.), sondern gleich schlafen gelegt.
  if (ausklangLaeuft()) {
    ausklangBeenden();
    return;
  }
  try {
    void c.resume().catch(() => undefined);
  } catch {
    /* die nächste Geste versucht es wieder */
  }
}

/**
 * Aus einer Geste heraus aufrufen: legt den Kontext an (nur wenn der Schalter
 * an ist — oder gerade eingeschaltet wird) und weckt ihn. Ein stilles Sample hinterher macht ihn auf iOS
 * hörbar „echt". Wirft nie.
 */
export function entsperrePcm(schalterGerade = false): void {
  if (schalterGerade) nutzerPause = false;
  else if (nutzerPause) return;
  try {
    // `schalterGerade`: der Klick auf den Schalter selbst — gespeichert ist er in diesem Augenblick noch nicht.
    if (!geteilterKontext && !schalterGerade && loadSettings().ttsPcmBeta !== true) return;
    // In der App braucht Ton keine Geste — dort weckt eine Berührung nur einen Kontext, den ein Stück gerade hält
    // (sonst nähme jede Berührung dem Wach-Wort das Mikrofon, siehe `kontextRuhen`).
    if (inDerApp()) {
      // Chat-Wischen beim nativen Vorlesen: ein `resume()` hier ließe WebKit die Tonsitzung nehmen (geste-wache.ts).
      if (wuerdeGesteWecken() && !gesteDarfWecken("pcm-resume")) return;
      nimmKontextWiederAuf();
      return;
    }
    const c = pcmKontext();
    if (!c || c.state === "running") return;
    void c.resume().catch(() => undefined);
    const stille = c.createBuffer(1, 1, c.sampleRate);
    const q = c.createBufferSource();
    q.buffer = stille;
    q.connect(c.destination);
    q.start(0);
  } catch {
    /* Entsperren ist best-effort */
  }
}

export interface PcmUmgebung {
  holen: typeof fetch;
  kontext: (rate: number) => AudioContext | null;
  /** Die Uhr in ms — als Naht für Tests. */
  jetzt: () => number;
}

export function standardUmgebung(): PcmUmgebung {
  return {
    holen: (...a) => fetch(...a),
    kontext: (rate) => pcmKontext(rate),
    jetzt: () => Date.now(),
  };
}

/** Was beim ersten hörbaren Ton gemessen wurde (alles in ms seit `start()`). */
/** Latenz des Ausgangs in ms (outputLatency + baseLatency); null, wenn der Browser keine meldet. */
export function ausgangMs(c: { outputLatency?: number; baseLatency?: number } | null | undefined): number | null {
  const aus = c?.outputLatency;
  if (typeof aus !== "number" || !Number.isFinite(aus) || aus < 0) return null;
  const basis = typeof c?.baseLatency === "number" && Number.isFinite(c.baseLatency) && c.baseLatency > 0 ? c.baseLatency : 0;
  return Math.round((aus + basis) * 1000);
}

export interface PcmMessung {
  /** Erstes Paket vom Server. */
  paketMs: number;
  /** Erster Block geplant (= hörbar, bis auf ANLAUF_S und die Latenz der Ausgabe). */
  tonMs: number;
  /** So viel Ton lag beim Start im Vorrat. */
  vorlaufMs: number;
  /** Latenz des Ausgangs laut Kontext (outputLatency + baseLatency, ms); fehlt, wenn der Browser sie nicht meldet. Nur Messung (NEUN_MESSUNG_FEIN). */
  ausMs?: number | null;
}

export type PcmEnde =
  | {
      art: "ende";
      /** Länge des Stücks in Sekunden. */
      dauerS: number;
      /** Wie oft der Vorrat leer lief (Lücke ≥ AUSSETZER_MS). */
      aussetzer: number;
      /** Summe aller Lücken in ms. */
      lueckeMs: number;
    }
  | { art: "abbruch"; dauerS: number; aussetzer: number; lueckeMs: number }
  | {
      /** Der Datei-Weg muss ran — ab `abS` Sekunden (0 = von vorn). */
      art: "datei";
      grund: string;
      abS: number;
      /** true: noch kein Ton gespielt — nichts zu zählen, nur umzuschalten. */
      vorErstemPaket: boolean;
      /** Gehört der Grund zu den echten Fehlern (zählt für den Rückfall) oder war es eine Absage des Servers (409)? */
      fehler: boolean;
      aussetzer: number;
      lueckeMs: number;
      /** K1: die Weck-Frist lief ab (nur App) — Zustand, Dauer, Folge, und ob diese eine als Fehler zählt. */
      weck?: { state: string; ms: number; folge: number; zaehlt: boolean };
    };

/** Eine Absage des Servers (409): der Satz geht den Datei-Weg, das ist kein Fehler. */
class Absage extends Error {
  constructor(public readonly status: number, public readonly grund: string) {
    super(`pcm ${status} ${grund}`);
  }
}

/** Liegt die Seite im Hintergrund? (Ohne `document`, etwa in Tests ohne DOM: nein.) */
function seiteVersteckt(): boolean {
  try {
    return typeof document !== "undefined" && document.visibilityState === "hidden";
  } catch {
    return false;
  }
}

export class PcmStueck {
  private readonly ac = new AbortController();
  private gestartet = false;
  private bloecke: Float32Array[] = [];
  private geplant = 0;
  private gesamtS = 0;
  private rate = PCM_RATE_STANDARD;
  private gainDb: number | null = null;
  private hoehenDb = -2.5;
  private hoehenHz = 6000;
  private ende = false;
  private fehler: unknown = null;
  private tStart = 0;
  private tErstesPaket: number | null = null;
  private letztesPaket = 0;
  /** Messfühler „Wer stockt?“ (11.10.2026): größte Pause zwischen zwei Paketen (ms) … */
  private maxPaketLueckeMs = 0;
  /** … und größte Pause zwischen zwei Runden der Abspielschleife (ms; gesund ≤ rund 60) — ein blockierter Hauptlauf zeigt sich hier. */
  private maxSchleifeMs = 0;
  /** War die Seite während des Stücks versteckt? Dann drosselt der Browser die Zeitgeber, und `schleife` sagt nichts über den Hauptlauf. */
  private warVersteckt = false;
  private wecker: (() => void) | null = null;
  private quellen: AudioBufferSourceNode[] = [];
  private ketteAb: (() => void) | null = null;
  private stopp = false;
  private angehalten = false;
  /** Zählt die Pausen-Aufrufe: nur das jüngste `resume()` hebt das Angehalten auf. */
  private pausenZaehler = 0;
  private ctx: AudioContext | null = null;
  private startZeit = 0;
  /** Ist der erste Ton eingeplant (`startZeit` gilt)? Vorher stünde `gespieltS()` auf der Uhr des Kontexts. */
  private toent = false;
  private unterbrechung: string | null = null;
  private statechangeAb: (() => void) | null = null;
  private sitzungGehalten = false;
  /** Musste dieses Stück den Kontext aus der Ruhe der App wecken? (Pegelzeile `geweckt=`) */
  private geweckt = false;
  /** Wie lange das Wecken dauerte (ms), oder null, wenn der Kontext schon lief (Pegelzeile `weckMs=`, K5). */
  private weckMs: number | null = null;
  /** Messung am Ausgang (Zahlen für das Journal). */
  private readonly mess = { fenster: 0, summeQuadrat: 0, spitze: 0, stumm: 0, stummFolge: 0, stummSeit: 0, zeit: "ok" };
  private messKontext: AudioContext | null = null;
  /**
   * Die Warteschlange hängt hier ein: Scheitert das Stück VOR seinem ersten Paket (Absage 409, Fehler), lädt sie
   * sofort die Datei vor — sonst finge die Datei erst an zu rechnen, wenn der Satz dran ist (2,5–3,5 s Lücke).
   */
  beiVorFehler: (() => void) | null = null;
  /**
   * Ticket 2919 (a): Der Kontext läuft nicht mehr (iOS: `interrupted` beim Sperren, Anruf, Siri) — die Warteschlange
   * lädt dann sofort die Dateien der wartenden Stücke vor, nicht erst nach 15 s Wachhund.
   */
  beiKontextWeg: (() => void) | null = null;
  /** 16.10.2026: das Laden ist vorbei (ganz, gescheitert, abgebrochen) — die Schlange startet dann den nächsten Abruf (`ganzVorab`). */
  beiLadeEnde: (() => void) | null = null;
  /** V9: bekommt in der Abspielschleife (rund 20 Mal je Sekunde) den Effektivwert des Ausgangs, 0 solange nichts spielt (Pause, Warten). Eine Anzeige, kein Eingriff. */
  beiPegel: ((rms: number) => void) | null = null;
  /**
   * 17.10.2026 (Prüfauflage): Lädt dieses Stück gerade als VORABLADUNG (Schmied, Ganz-Modus)? Gefragt beim Beginn des
   * Abrufs, nicht beim Einreihen — reißt die Obergrenze und spricht der Schmied stückweise, laden seine restlichen Stücke
   * mit Vorrang wie jedes Vorlesen. `true` hängt `&vorab=1` an (server/soniox-schleuse.ts, Spur `vorab`). Ohne: nichts.
   */
  vorab: (() => boolean) | null = null;
  /** Die schon vorgeladene Datei desselben Satzes (`<audio>`), falls die Warteschlange eine angelegt hat. */
  dateiAudio: HTMLAudioElement | null = null;
  /**
   * 17.10.2026 (Stimme nativ, stimme-nativ.ts): die rohen s16le-Brocken so mitschreiben, wie sie kamen — spielt die App
   * die Stimme selbst, bekommt sie genau diese Bytes (nicht nach Float gewandelt). Nur, wenn die Schlange es verlangt
   * (Kippe AN und die App kennt `stimmeStueck`); sonst bleibt die Liste leer.
   */
  rohMerken = false;
  private roh: Uint8Array[] = [];
  private rohBytes = 0;

  constructor(
    readonly url: string,
    signal: AbortSignal,
    private readonly env: PcmUmgebung = standardUmgebung(),
  ) {
    if (signal.aborted) this.ac.abort();
    else signal.addEventListener("abort", () => this.stoppen(), { once: true });
  }

  /** Beginnt den Abruf (idempotent) — am besten schon beim Einreihen des Stücks. */
  start(): void {
    if (this.gestartet) return;
    this.gestartet = true;
    this.tStart = this.env.jetzt();
    void this.laden();
  }

  private weck(): void {
    const w = this.wecker;
    this.wecker = null;
    w?.();
  }

  private warte(maxMs: number): Promise<void> {
    return new Promise<void>((fertig) => {
      const t = setTimeout(() => {
        this.wecker = null;
        fertig();
      }, maxMs);
      this.wecker = () => {
        clearTimeout(t);
        fertig();
      };
    });
  }

  private async laden(): Promise<void> {
    try {
      const r = await this.env.holen(pcmAbrufAdresse(this.url, this.vorab?.() === true), {
        credentials: "same-origin",
        signal: this.ac.signal,
      });
      if (!r.ok) {
        let grund = `http${r.status}`;
        try {
          const j = (await r.json()) as { grund?: unknown };
          if (typeof j.grund === "string") grund = j.grund;
        } catch {
          /* kein JSON: der Statuscode genügt */
        }
        throw new Absage(r.status, grund);
      }
      const rate = Number(r.headers.get("X-Tts-Rate"));
      if (Number.isFinite(rate) && rate >= 8000 && rate <= 48000) this.rate = rate;
      const gain = Number(r.headers.get("X-Tts-Gain-Db"));
      // Ohne gültige Verstärkung würde das Stück zu leise — dann lieber die Datei.
      if (r.headers.get("X-Tts-Gain-Db") === null || !Number.isFinite(gain)) throw new Error("ohne Verstärkung");
      this.gainDb = gain;
      const hd = r.headers.get("X-Tts-Hoehen-Db");
      const hz = r.headers.get("X-Tts-Hoehen-Hz");
      if (hd !== null && Number.isFinite(Number(hd))) this.hoehenDb = Number(hd);
      if (hz !== null && Number.isFinite(Number(hz)) && Number(hz) > 0) this.hoehenHz = Number(hz);
      const leser = r.body?.getReader();
      if (!leser) throw new Error("kein Strom");
      let rest: number | null = null;
      for (;;) {
        const { done, value } = await leser.read();
        if (done) break;
        if (!value || value.length === 0) continue;
        if (this.rohMerken) {
          this.roh.push(value);
          this.rohBytes += value.length;
        }
        const z = pcmZuFloat(value, rest);
        rest = z.rest;
        if (z.samples.length > 0) {
          if (this.tErstesPaket === null) this.tErstesPaket = this.env.jetzt() - this.tStart;
          const jetztPaket = this.env.jetzt();
          if (this.letztesPaket > 0) this.maxPaketLueckeMs = Math.max(this.maxPaketLueckeMs, jetztPaket - this.letztesPaket);
          this.letztesPaket = jetztPaket;
          this.bloecke.push(z.samples);
          this.gesamtS += z.samples.length / this.rate;
        }
        this.weck();
      }
      this.ende = true;
    } catch (err) {
      if (!this.ac.signal.aborted) {
        this.fehler ??= err;
        this.meldeVorFehler();
      }
    }
    this.weck();
    try {
      this.beiLadeEnde?.();
    } catch {
      /* ein Hörer darf das Laden nie stören */
    }
  }

  /**
   * 16.10.2026 (Schmied „in einem Stück“, tts-queue.ts `ganzVorab`): ist das Laden vorbei — ganz da, gescheitert oder
   * abgebrochen? Dann wartet auf dieses Stück nichts mehr; ein gescheitertes geht beim Abspielen wie immer auf die Datei.
   */
  ladenVorbei(): boolean {
    return this.ende || this.fehler !== null || this.stopp;
  }

  /** Läuft der Abruf schon (`start()`)? */
  laedt(): boolean {
    return this.gestartet;
  }

  /** Scheiterte das Stück vor dem ersten Paket? Dann darf die Warteschlange die Datei schon vorladen. */
  private meldeVorFehler(): void {
    if (this.tErstesPaket !== null || this.stopp) return;
    try {
      this.beiVorFehler?.();
    } catch {
      /* eine Vorladung darf nie stören */
    }
  }

  /**
   * Der Stand für den nativen Weg (stimme-nativ.ts): Kopfzeilen, wie viele rohe Brocken da sind, ob der Strom zu Ende
   * ist, und warum er scheiterte (`null` = nicht gescheitert). Reiner Lesezugriff.
   */
  rohLage(): {
    brocken: number;
    /** Rohe Bytes insgesamt. */
    bytes: number;
    /** ms seit dem Beginn des Abrufs (`start()`), 0 davor — die Uhr von `paketMs`. */
    alterMs: number;
    fertig: boolean;
    fehler: string | null;
    paketMs: number | null;
    rate: number;
    gainDb: number | null;
    hoehenDb: number;
    hoehenHz: number;
  } {
    const f = this.fehler;
    const fehler = this.stopp
      ? "abbruch"
      : f === null
        ? null
        : f instanceof Absage
          ? f.grund
          : f instanceof Error
            ? f.message.slice(0, 60)
            : "unbekannt";
    return {
      brocken: this.roh.length,
      bytes: this.rohBytes,
      alterMs: this.gestartet ? this.env.jetzt() - this.tStart : 0,
      fertig: this.ende,
      fehler,
      paketMs: this.tErstesPaket,
      rate: this.rate,
      gainDb: this.gainDb,
      hoehenDb: this.hoehenDb,
      hoehenHz: this.hoehenHz,
    };
  }

  /** Die rohen Brocken ab Nummer `ab` (stimme-nativ.ts schickt sie der Reihe nach an die App). */
  rohAb(ab: number): Uint8Array[] {
    return this.roh.slice(ab);
  }

  /** Höchstens `maxMs` auf neue Brocken, das Ende, einen Fehler oder den Abbruch warten. */
  rohWarten(maxMs: number): Promise<void> {
    if (this.stopp || this.ende || this.fehler !== null) return Promise.resolve();
    return this.warte(maxMs);
  }

  /** So viel Ton liegt heruntergeladen da (Sekunden). */
  vorratS(): number {
    return this.gesamtS;
  }

  /** Ist der Strom bis zu seinem Ende heruntergeladen? Dann ist `vorratS()` die Länge des Stücks. */
  fertigGeladen(): boolean {
    return this.ende;
  }

  /** Wie viele Sekunden bisher gespielt wurden (0, solange noch kein Ton eingeplant ist). */
  gespieltS(): number {
    if (!this.ctx || !this.toent) return 0;
    return Math.max(0, Math.min(this.gesamtS, this.ctx.currentTime - this.startZeit));
  }

  /**
   * Die Messung dieses Stroms als Journalzeile — nur Zahlen — oder null, wenn nie gemessen wurde (nie gespielt).
   * `rms`/`peak` in dBFS HINTER der Klangkette; `stumm` = Fenster ohne Signal, obwohl Ton eingeplant war.
   */
  pegelZeile(): string | null {
    const m = this.mess;
    const c = this.messKontext ?? this.ctx;
    if (!c || m.fenster === 0) return null;
    const db = (x: number, f: number): string => (x > 0 ? (f * Math.log10(x)).toFixed(1) : "-inf");
    const lat = (v: unknown): string => (typeof v === "number" && Number.isFinite(v) ? v.toFixed(3) : "-");
    const k = c as unknown as { baseLatency?: number; outputLatency?: number; sampleRate?: number };
    return (
      `[tts-pcm] pegel rms=${db(m.summeQuadrat / m.fenster, 10)}dB peak=${db(m.spitze, 20)}dB fenster=${m.fenster} ` +
      `stumm=${m.stumm} state=${c.state} session=${sitzungBericht()} basis=${lat(k.baseLatency)}s aus=${lat(k.outputLatency)}s ` +
      `rate=${k.sampleRate ?? "-"} uhr=${m.zeit} geweckt=${this.geweckt ? "ja" : "nein"} weckMs=${this.weckMs ?? "-"} ` +
      `ausklang=${ausklangDauerMs(k)}`
    );
  }

  /**
   * Wer stockt — das Netz oder die Seite? (11.10.2026) Die Journalzeile für ein Stück MIT Aussetzer, nur Zahlen:
   * `paket` = größte Pause zwischen zwei Paketen, `schleife` = größte Pause zwischen zwei Runden der Abspielschleife.
   * Große Schleifen-Pause: der Hauptlauf der Seite war blockiert (die Paket-Pause ist dann mit blockiert, denn die Pakete werden
   * im selben Hauptlauf gelesen). Große Paket-Pause bei kleiner Schleifen-Pause: der Strom kam spät (Netz, Server).
   * War die Seite während des Stücks versteckt, steht `seite=versteckt` dabei: der Browser drosselt dann die Zeitgeber (rund 1 s
   * je Runde), die Schleifen-Pause ist nicht zu werten, und Runden, die ins Versteckte reichen, zählen für sie nicht.
   */
  stoerungsZeile(): string {
    // Versteckte Seite: die Schleifen-Pause ist nicht zu werten (gedrosselte Zeitgeber) — das steht dabei.
    return (
      `[tts-pcm] stoerung paketLuecke=${Math.round(this.maxPaketLueckeMs)}ms schleife=${Math.round(this.maxSchleifeMs)}ms` +
      (this.warVersteckt ? " seite=versteckt" : "")
    );
  }

  /** Alle Quellen stoppen und den Abruf beenden — für Abbruch, neue Antwort, andere Seite. */
  stoppen(): void {
    if (this.stopp) return;
    this.stopp = true;
    try {
      this.ac.abort();
    } catch {
      /* schon zu */
    }
    this.quellenStoppen();
    this.weck();
  }

  /** Der Pegel für die Anzeige am Kreis (V9): Effektivwert des Ausgangs, 0 solange nichts spielt. Fehler der Anzeige bleiben außen vor. */
  private meldePegel(spielt: boolean, analyse: AnalyserNode | null, abtast: Float32Array<ArrayBuffer> | null): void {
    if (!this.beiPegel || !analyse || !abtast) return;
    try {
      this.beiPegel(spielt ? rmsVon(analyse, abtast) : 0);
    } catch {
      /* eine Anzeige darf die Wiedergabe nie stören */
    }
  }

  /** Verwerfen, ohne je gespielt zu haben (die Seite ging in den Hintergrund). */
  verwerfen(): void {
    this.stoppen();
  }

  /** Das Stück verlassen, weil die Seite in den Hintergrund geht — der Rest kommt aus der Datei. */
  unterbrechen(grund: string): void {
    if (this.stopp || this.unterbrechung) return;
    this.unterbrechung = grund;
    this.weck();
  }

  /**
   * Nutzer-Pause: der Kontext steht, die Position bleibt. Beim Fortsetzen gilt das Stück erst als „nicht angehalten“, wenn `resume()` fertig ist: ein
   * spätes `statechange` des vorigen `suspend()` (schnelles Pause-Weiter-Pause) darf es sonst nicht als Unterbrechung verlassen.
   */
  pausieren(an: boolean): void {
    const c = this.ctx;
    const nr = ++this.pausenZaehler;
    if (an || !c) this.angehalten = an;
    if (!c) return;
    try {
      void (an ? c.suspend() : c.resume())
        .catch(() => undefined)
        .finally(() => {
          if (!an && nr === this.pausenZaehler) this.angehalten = false;
        });
    } catch {
      this.angehalten = an;
    }
    this.weck();
  }

  private quellenStoppen(): void {
    for (const q of this.quellen) {
      try {
        q.stop();
      } catch {
        /* nie gestartet oder schon zu Ende */
      }
      try {
        q.disconnect();
      } catch {
        /* egal */
      }
    }
    this.quellen = [];
    this.ketteAb?.();
    this.ketteAb = null;
    this.statechangeAb?.();
    this.statechangeAb = null;
  }

  /**
   * Spielt das Stück. Erst warten, bis 0,5 s Vorrat da sind (oder der Strom
   * zu Ende ist), dann lückenlos planen, was kommt.
   *
   * `vorErstemTon` läuft synchron VOR dem ersten `start()` (dort meldet die
   * Warteschlange „Ton der Seite spielt", `tts-audio-start`), `beiErstemTon`
   * danach mit den Messwerten. `vorWecken` läuft, bevor der Kontext geweckt
   * wird (dort meldet die Schlange die Miete des Seitentons, Punkt 5).
   */
  async spiele(o: {
    vorWecken?: () => void;
    vorErstemTon?: () => void;
    beiErstemTon?: (m: PcmMessung) => void;
    /** Vorrat (s) vor dem Start; nie unter `START_VORRAT_S`. Fehlt er, gilt dieser (tts-pcm-vorrat.ts: größer nach einem Aussetzer). */
    startVorratS?: number;
  }): Promise<PcmEnde> {
    try {
      return await this.spieleInnen(o);
    } finally {
      if (this.sitzungGehalten) {
        this.sitzungGehalten = false;
        sitzungLoslassen();
      }
    }
  }

  private async spieleInnen(o: {
    vorWecken?: () => void;
    vorErstemTon?: () => void;
    beiErstemTon?: (m: PcmMessung) => void;
    startVorratS?: number;
  }): Promise<PcmEnde> {
    this.start();
    const tWarte = this.env.jetzt();
    // Der Vorrat vor dem Start: mindestens 0,5 s; größer nur, wenn die Warteschlange es nach einem Aussetzer verlangt.
    const vorrat = Number.isFinite(o.startVorratS) ? Math.max(START_VORRAT_S, o.startVorratS as number) : START_VORRAT_S;
    let tBasis: number | null = null;
    /** Reicht der Vorrat? Der Mindest-Vorrat immer; der größere nur, solange seine Frist (ab dem Mindest-Vorrat) läuft. */
    const vorratDa = (): boolean => {
      if (this.gesamtS < START_VORRAT_S) return false;
      if (this.gesamtS >= vorrat) return true;
      tBasis ??= this.env.jetzt();
      return this.env.jetzt() - tBasis >= VORRAT_FRIST_MS;
    };
    while (!this.stopp && !this.unterbrechung && !vorratDa() && !this.ende && !this.fehler) {
      await this.warte(250);
      if (this.tErstesPaket === null && !this.fehler && this.env.jetzt() - tWarte > ERSTES_PAKET_FRIST_MS) {
        // Kein erstes Paket in der Frist: der Datei-Weg (ein Fehler, er zählt für den Rückfall).
        this.fehler = new Error("erstes-paket-frist");
        try {
          this.ac.abort();
        } catch {
          /* schon zu */
        }
        this.meldeVorFehler();
      }
    }
    // Nutzer-Pause WÄHREND des Wartens auf den Vorrat: noch kein Kontext, aber „Pausiert“ steht in der Anzeige — nichts planen.
    while (this.angehalten && !this.stopp && !this.unterbrechung) await this.warte(200);
    const leer = (): { aussetzer: number; lueckeMs: number } => ({ aussetzer: 0, lueckeMs: 0 });
    if (this.stopp) return { art: "abbruch", dauerS: 0, ...leer() };
    if (this.unterbrechung) {
      this.stoppen();
      return { art: "datei", grund: this.unterbrechung, abS: 0, vorErstemPaket: true, fehler: false, ...leer() };
    }
    if (this.gesamtS === 0 || this.gainDb === null) {
      const f = this.fehler;
      const absage = f instanceof Absage;
      this.stoppen();
      return {
        art: "datei",
        grund: absage ? f.grund : f instanceof Error ? f.message.slice(0, 60) : this.ende ? "leer" : "unbekannt",
        abS: 0,
        vorErstemPaket: true,
        // Eine Absage (409) ist Alltag; ein 5xx, Netzfehler oder ein leerer Strom ist ein Fehler.
        fehler: !(absage && f.status === 409),
        ...leer(),
      };
    }
    const ctx = this.env.kontext(this.rate);
    if (!ctx) {
      this.stoppen();
      return { art: "datei", grund: "kein-webaudio", abS: 0, vorErstemPaket: true, fehler: true, ...leer() };
    }
    this.ctx = ctx;
    // Die Tonsitzung auf `playback` — VOR dem Aufwecken des Kontexts (Stummschalter gilt für Web Audio, nicht für <audio>).
    const ausRuhe = kontextSchlaeft();
    const schlaf = schlafVersprechen;
    this.geweckt = ausRuhe;
    // Punkt 5: die Miete des Seitentons VOR dem Wecken — das Native erfährt, dass die Seite gleich spielt, bevor
    // der Kontext die Tonsitzung nimmt.
    try {
      o.vorWecken?.();
    } catch {
      /* eine Meldung darf nie stören */
    }
    sitzungHalten();
    this.sitzungGehalten = true;
    const app = inDerApp();
    if (ausRuhe || ctx.state !== "running") {
      const tWeck = this.env.jetzt();
      let fristAus = false;
      const fristUhr = new Promise<void>((r) =>
        setTimeout(() => {
          fristAus = true;
          r();
        }, weckFrist()),
      );
      // K4: schlief er absichtlich, erst das `suspend()` abwarten (es kann noch unterwegs sein) und dann IMMER
      // `resume()` — auch wenn er noch `running` meldet: sonst überholte das späte suspend das Stück.
      if (ausRuhe && schlaf) await Promise.race([schlaf, fristUhr]);
      if (!fristAus) {
        await Promise.race([ctx.resume().catch(() => undefined), fristUhr]);
      }
      this.weckMs = Math.round(this.env.jetzt() - tWeck);
    }
    // „interrupted" kennt nur iOS (Anruf, Siri) — wie „suspended" ein Fall für den Datei-Weg.
    if ((ctx.state as string) !== "running") {
      const state = ctx.state as string;
      const unterbrochen = state === "interrupted";
      this.stoppen();
      if (app && !unterbrochen) {
        // K1: in der App ist ein nicht aufgewachter Kontext eine Weck-Frist, kein Fehler des Weges (`kontext-` →
        // Unterbrechungs-Zweig der Schlange). Erst die dritte in Folge zählt EINMAL als Fehler vor dem Start.
        weckFristFolge += 1;
        const weck = {
          state,
          ms: this.weckMs ?? 0,
          folge: weckFristFolge,
          zaehlt: weckFristFolge === WECK_FRIST_FOLGE_ZAEHLT,
        };
        return { art: "datei", grund: "kontext-weckfrist", abS: 0, vorErstemPaket: true, fehler: false, weck, ...leer() };
      }
      // `interrupted` (Anruf, Siri, Sperren) ist ein Zustand des Telefons, kein Fehler des Weges (Ticket 2919 d);
      // `suspended` trotz Entsperrversuch ist einer (im Browser).
      return { art: "datei", grund: `kontext-${state}`, abS: 0, vorErstemPaket: true, fehler: !unterbrochen, ...leer() };
    }
    if (app) weckFristFolge = 0;
    if (this.stopp) return { art: "abbruch", dauerS: 0, ...leer() };
    // Läuft der Kontext während des Stücks nicht mehr, sofort melden (statt auf die 2,5 s des Wachpostens zu warten).
    const beiWechsel = (): void => {
      if (this.stopp || this.angehalten) return;
      if ((ctx.state as string) === "running" || ctx.state === "closed") return;
      this.unterbrechen(`kontext-${ctx.state}`);
      try {
        this.beiKontextWeg?.();
      } catch {
        /* eine Vorladung darf nie stören */
      }
    };
    try {
      ctx.addEventListener("statechange", beiWechsel);
      this.statechangeAb = () => ctx.removeEventListener("statechange", beiWechsel);
    } catch {
      /* ohne EventTarget gilt der Wachposten unten */
    }

    // Die Kette: Quelle → Höhen-Shelf → Verstärkung → Begrenzer → Sicherheitskennlinie → Ausgang.
    const shelf = ctx.createBiquadFilter();
    shelf.type = "highshelf";
    shelf.frequency.value = this.hoehenHz;
    shelf.gain.value = this.hoehenDb;
    const verstaerkung = ctx.createGain();
    verstaerkung.gain.value = Math.pow(10, browserVerstaerkung(this.gainDb) / 20);
    const begrenzer = ctx.createDynamicsCompressor();
    begrenzer.threshold.value = LIMITER.threshold;
    begrenzer.knee.value = LIMITER.knee;
    begrenzer.ratio.value = LIMITER.ratio;
    begrenzer.attack.value = LIMITER.attack;
    begrenzer.release.value = LIMITER.release;
    const netz = ctx.createWaveShaper();
    netz.curve = sicherheitsKurve();
    netz.oversample = "2x";
    shelf.connect(verstaerkung);
    verstaerkung.connect(begrenzer);
    begrenzer.connect(netz);
    netz.connect(ctx.destination);
    // Der Messfühler: ein AnalyserNode HINTER der Kette, über einen stummen Gain an den Ausgang gehängt (damit jede
    // Web-Audio-Fassung ihn zieht). Er verändert den Ton nicht.
    let analyse: AnalyserNode | null = null;
    let abtast: Float32Array<ArrayBuffer> | null = null;
    let tot: GainNode | null = null;
    try {
      analyse = ctx.createAnalyser();
      analyse.fftSize = ANALYSE_FENSTER;
      tot = ctx.createGain();
      tot.gain.value = 0;
      netz.connect(analyse);
      analyse.connect(tot);
      tot.connect(ctx.destination);
      abtast = new Float32Array(ANALYSE_FENSTER);
      this.messKontext = ctx;
    } catch {
      analyse = null;
    }
    this.ketteAb = () => {
      try {
        shelf.disconnect();
        verstaerkung.disconnect();
        begrenzer.disconnect();
        netz.disconnect();
        analyse?.disconnect();
        tot?.disconnect();
      } catch {
        /* egal */
      }
    };
    /** Die Blöcke mit Beginn, Ende und Eingangspegel — daran misst sich, ob der Ausgang laut sein MÜSSTE. */
    const blockInfo: Array<{ von: number; bis: number; rms: number }> = [];
    let uhrWert = ctx.currentTime;
    let uhrSeit = this.env.jetzt();

    let aussetzer = 0;
    let lueckeMs = 0;
    this.startZeit = ctx.currentTime + ANLAUF_S;
    this.toent = true;
    let nextStart = this.startZeit;
    let ersterTon = false;
    const vorlaufMs = Math.round(this.gesamtS * 1000);
    let rueckfallGrund: string | null = null;

    const planeNeue = (): void => {
      while (this.geplant < this.bloecke.length) {
        const samples = this.bloecke[this.geplant++];
        const buf = ctx.createBuffer(1, samples.length, this.rate);
        buf.getChannelData(0).set(samples);
        const q = ctx.createBufferSource();
        q.buffer = buf;
        q.connect(shelf);
        const jetzt = ctx.currentTime;
        if (ersterTon && nextStart < jetzt) {
          // Der Vorrat war leer, bevor dieses Stück Ton ankam: eine Lücke im Ohr.
          const luecke = (jetzt - nextStart) * 1000;
          if (luecke >= AUSSETZER_MS) {
            aussetzer += 1;
            lueckeMs += luecke;
          }
          // Neu aufsetzen, sonst spielt Web Audio den verspäteten Block „in der Vergangenheit" sofort an.
          this.startZeit += jetzt + 0.01 - nextStart;
          nextStart = jetzt + 0.01;
        }
        if (!ersterTon) {
          ersterTon = true;
          try {
            o.vorErstemTon?.();
          } catch {
            /* eine Meldung darf die Wiedergabe nie stören */
          }
        }
        q.start(nextStart);
        {
          let sq = 0;
          let n = 0;
          for (let i = 0; i < samples.length; i += 4) {
            sq += (samples[i]) ** 2;
            n++;
          }
          blockInfo.push({ von: nextStart, bis: nextStart + buf.duration, rms: Math.sqrt(sq / Math.max(1, n)) });
          if (blockInfo.length > 40) blockInfo.shift();
        }
        nextStart += buf.duration;
        this.quellen.push(q);
        q.onended = () => {
          const i = this.quellen.indexOf(q);
          if (i >= 0) this.quellen.splice(i, 1);
          try {
            q.disconnect();
          } catch {
            /* egal */
          }
        };
      }
    };

    planeNeue();
    try {
      o.beiErstemTon?.({
        paketMs: this.tErstesPaket ?? 0,
        tonMs: this.env.jetzt() - this.tStart,
        vorlaufMs,
        ausMs: ausgangMs(ctx),
      });
    } catch {
      /* eine Sonde darf die Wiedergabe nie stören */
    }

    // Warten, bis alles geplant UND abgespielt ist — oder etwas dazwischenkommt.
    let stillSeit: number | null = null;
    let letzteRunde = this.env.jetzt();
    let letzteSichtbar = !seiteVersteckt();
    for (;;) {
      {
        // Nur Runden zählen, die ganz im Sichtbaren lagen: eine versteckte Seite hat gedrosselte Zeitgeber (≈ 1 s je Runde).
        const jetztRunde = this.env.jetzt();
        const sichtbar = !seiteVersteckt();
        if (sichtbar && letzteSichtbar) this.maxSchleifeMs = Math.max(this.maxSchleifeMs, jetztRunde - letzteRunde);
        else this.warVersteckt = true;
        letzteRunde = jetztRunde;
        letzteSichtbar = sichtbar;
      }
      if (this.stopp) return { art: "abbruch", dauerS: this.gesamtS, aussetzer, lueckeMs };
      planeNeue();
      if (this.unterbrechung) {
        const ab = this.gespieltS();
        this.stoppen();
        return { art: "datei", grund: this.unterbrechung, abS: ab, vorErstemPaket: false, fehler: false, aussetzer, lueckeMs };
      }
      const jetzt = ctx.currentTime;
      const spielt = (ctx.state as string) === "running" && !this.angehalten;
      this.meldePegel(spielt && jetzt >= this.startZeit, analyse, abtast);
      if (this.ende && this.geplant >= this.bloecke.length && jetzt >= nextStart) {
        this.stoppen();
        return { art: "ende", dauerS: this.gesamtS, aussetzer, lueckeMs };
      }
      if (this.fehler && !this.ende && this.geplant >= this.bloecke.length && jetzt >= nextStart) {
        // Der Strom riss mitten im Stück: der Rest kommt aus der Datei, ab dem, was schon gespielt wurde.
        const ab = this.gesamtS;
        rueckfallGrund = this.fehler instanceof Error ? `strom:${this.fehler.message.slice(0, 40)}` : "strom";
        this.stoppen();
        return { art: "datei", grund: rueckfallGrund, abS: ab, vorErstemPaket: false, fehler: true, aussetzer, lueckeMs };
      }
      // Der Kontext steht (iOS: Anruf, Siri, Sperren) und keine Nutzer-Pause: nach kurzer Frist die Datei.
      if (!spielt && !this.angehalten) {
        stillSeit ??= this.env.jetzt();
        if (this.env.jetzt() - stillSeit > 2500) {
          const ab = this.gespieltS();
          this.stoppen();
          return { art: "datei", grund: `kontext-${ctx.state}`, abS: ab, vorErstemPaket: false, fehler: false, aussetzer, lueckeMs };
        }
      } else {
        stillSeit = null;
      }
      // Der Strom schweigt zu lange, ohne dass er riss (Netz weg ohne Fehler).
      if (!this.ende && !this.fehler && !this.angehalten && this.env.jetzt() - this.letztesPaket > STROM_STILLE_MS && jetzt >= nextStart) {
        const ab = this.gesamtS;
        this.stoppen();
        return { art: "datei", grund: "strom-still", abS: ab, vorErstemPaket: false, fehler: true, aussetzer, lueckeMs };
      }
      // Messen: Spielt der Kontext, ist Ton eingeplant und laut — was kommt hinten heraus? (Journal `[tts-pcm] pegel`.)
      if (spielt && jetzt >= this.startZeit && jetzt < nextStart) {
        const aktiv = blockInfo.find((b) => b.von <= jetzt && jetzt < b.bis);
        const jetztMs = this.env.jetzt();
        if (jetzt - uhrWert >= 0.05) {
          uhrWert = jetzt;
          uhrSeit = jetztMs;
        } else if (jetztMs - uhrSeit > UHR_STEHT_MS) {
          // „running", Ton eingeplant, und die Uhr rückt nicht vor: der Kontext zieht nichts (iOS-Fehlerbild).
          this.mess.zeit = "steht";
          const ab = this.gespieltS();
          this.stoppen();
          return { art: "datei", grund: "uhr-steht", abS: ab, vorErstemPaket: false, fehler: true, aussetzer, lueckeMs };
        }
        if (analyse && abtast && aktiv && aktiv.rms > STUMM_BLOCK_RMS) {
          analyse.getFloatTimeDomainData(abtast);
          let sq = 0;
          let sp = 0;
          for (let i = 0; i < abtast.length; i++) {
            const v = abtast[i];
            sq += v * v;
            if (Math.abs(v) > sp) sp = Math.abs(v);
          }
          const m = this.mess;
          m.fenster += 1;
          m.summeQuadrat += sq / abtast.length;
          if (sp > m.spitze) m.spitze = sp;
          if (sp < STUMM_SPITZE) {
            m.stumm += 1;
            if (m.stummFolge === 0) m.stummSeit = jetztMs;
            m.stummFolge += 1;
            if (m.stummFolge >= 6 && jetztMs - m.stummSeit >= STUMM_MS) {
              // Der Browser spielt, hinten kommt nichts heraus: lieber die Datei, damit etwas zu hören ist.
              const ab = this.gespieltS();
              this.stoppen();
              return { art: "datei", grund: "stumm", abS: ab, vorErstemPaket: false, fehler: true, aussetzer, lueckeMs };
            }
          } else {
            m.stummFolge = 0;
          }
        }
      } else {
        // Pause, Unterbrechung, Warten auf Daten: die Uhr-Wache und die Stumm-Folge beginnen danach von vorn
        // (sonst gälte die Pausenzeit als stehende Uhr).
        uhrWert = jetzt;
        uhrSeit = this.env.jetzt();
        this.mess.stummFolge = 0;
      }
      await this.warte(50);
    }
  }
}
